// Team analysis page.
//
// Layout:
//   PageTitle
//   §01  Team       (slot strip + multiselect, exactly TEAM_SIZE mons)
//   §02  Observables strip (Score, Coherence, Corpus)
//   §03  Pairwise coupling decomposition (15 rows)
//   §04  Top single swaps from this team (TOP_SINGLE_SWAPS rows, independent)
//   §05  Greedy critique — single-swap chain
//
// All math is deterministic (no MCMC); recomputes inline when inputs change.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  GREEDY_MAX_SWAPS,
  TEAM_SIZE,
  TOP_SINGLE_SWAPS,
} from "../constants";
import { useModel } from "../state/ModelContext";
import { usePageState, type RosterSlot } from "../state/PageStateContext";
import { PageTitle, SectionLabel, StatStrip } from "../render/atoms";
import { RosterEditor } from "../components/RosterEditor";
import type { IsingModel } from "../sampler/types";
import {
  intraTeamSumJ,
  pairwiseJRows,
  teamObservables,
} from "../render/observables";
import { nearestObserved } from "../render/corpus";
import { percentileTitle } from "../render/corpusScore";
import { rankSingleSwaps } from "../sampler/rank";
import { greedyOptimize } from "../sampler/greedy";
import { buildPartialPaste, formatSigned } from "../render/format";
import { speciesToSlug } from "../render/sprite-url";
import { buildSlugIndex, matchPaste, resolveFeature } from "../render/vocab-match";
import { decodeCore, encodeCore } from "../render/shareLink";
import { PairwiseJTable } from "../analysis/PairwiseJTable";
import { SwapsTable } from "../analysis/SwapsTable";
import { ChainTable } from "../analysis/ChainTable";
import { ScrollX } from "../components/ScrollX";

/** Feature indices → roster slots (each a feature pin). Analysis is
 * feature-level, so every entry carries a concrete item. */
function idxsToRoster(model: IsingModel, idxs: readonly number[]): RosterSlot[] {
  return idxs.map((i) => ({ site: model.siteOf[i], feature: i }));
}

export function AnalysisPage() {
  const { model, teamCounts, corpusScoreIndex, status, modelId, setModelId } =
    useModel();
  const [searchParams, setSearchParams] = useSearchParams();
  const { analysis, setAnalysis } = usePageState();
  const { roster } = analysis;
  // The complete team = roster slots with an item pinned. Species-only slots
  // are in-progress picks that don't count until an item is chosen.
  const teamIdxs = useMemo(
    () => roster.filter((s) => s.feature !== null).map((s) => s.feature as number),
    [roster],
  );

  // The share token currently reflected in (or being applied from) the URL.
  // Guards the decode/live-sync handshake: decode marks a token applied,
  // live-sync skips writing while an unapplied incoming token is pending.
  const appliedTokenRef = useRef<string | null>(null);

  // Decode a shared link into state. Accepts the slug token (?t=) and a
  // legacy ?team= index list. Switches the model first if the token names a
  // different one, then applies once the matching model is ready.
  useEffect(() => {
    if (status !== "ready" || !model) return;
    const token = searchParams.get("t");
    const legacyTeam = searchParams.get("team");
    const key = token ?? (legacyTeam ? `team:${legacyTeam}` : null);
    if (!key || key === appliedTokenRef.current) return;

    if (token) {
      const decoded = decodeCore(token);
      if (!decoded) {
        appliedTokenRef.current = key;
        return;
      }
      if (decoded.modelId !== modelId) {
        setModelId(decoded.modelId);
        return; // re-run once the new model is ready
      }
      const slugIndex = buildSlugIndex(model);
      const idxs: number[] = [];
      for (const f of decoded.features) {
        const r = resolveFeature(slugIndex, model, f.speciesSlug, f.itemSlug);
        if (r.idx !== null) idxs.push(r.idx);
      }
      appliedTokenRef.current = key;
      setAnalysis({
        roster: idxsToRoster(model, idxs.slice(0, TEAM_SIZE)),
      });
      return;
    }

    // Legacy ?team=idx,idx fallback.
    const idxs = legacyTeam!
      .split(",")
      .map(Number)
      .filter((i) => !isNaN(i) && i >= 0 && i < model.V);
    appliedTokenRef.current = key;
    if (idxs.length > 0)
      setAnalysis({ roster: idxsToRoster(model, idxs.slice(0, TEAM_SIZE)) });
  }, [status, model, modelId, searchParams, setAnalysis, setModelId]);

  // Live-sync the URL from state (debounced so rapid edits settle into
  // one write). Skipped while an incoming token is still pending decode.
  const teamKey = useMemo(
    () => [...teamIdxs].sort((a, b) => a - b).join(","),
    [teamIdxs],
  );
  useEffect(() => {
    if (!model) return;
    const incoming = searchParams.get("t");
    if (incoming && incoming !== appliedTokenRef.current) return;
    // An emptied team clears the token instead of leaving the old one in the
    // URL, which a reload would otherwise decode back into the page.
    const token = teamIdxs.length > 0 ? encodeCore(modelId, teamIdxs, model) : "";
    if (token === (searchParams.get("t") ?? "")) return;
    const handle = setTimeout(() => {
      appliedTokenRef.current = token;
      setSearchParams(token ? { t: token } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, modelId, teamKey]);

  // Import a pokepaste from the clipboard into the team.
  const [importMsg, setImportMsg] = useState<{
    error: string | null;
    warnings: string[];
  } | null>(null);
  const handleImport = useCallback(async () => {
    if (!model) return;
    try {
      const text = await navigator.clipboard.readText();
      const { idxs, errors, warnings } = matchPaste(model, text);
      if (idxs.length > 0)
        setAnalysis({ roster: idxsToRoster(model, idxs.slice(0, TEAM_SIZE)) });
      setImportMsg({ error: errors[0] ?? null, warnings });
    } catch {
      setImportMsg({ error: "Couldn't read the clipboard.", warnings: [] });
    }
  }, [model, setAnalysis]);

  const teamSorted = useMemo(
    () => [...teamIdxs].sort((a, b) => a - b),
    [teamIdxs],
  );

  // Species/item uniqueness validation (inert under a species-only model,
  // where species are unique by construction and itemOf is all-null).
  const uniquenessError = useMemo<string | null>(() => {
    if (!model) return null;
    const seenSp = new Map<string, string>();
    const seenIt = new Map<string, string>();
    for (const i of teamIdxs) {
      const sp = model.speciesOf[i];
      if (seenSp.has(sp)) {
        return `Two entries for species ${sp} (${seenSp.get(sp)}, ${model.vocab[i]}). Pick one variant.`;
      }
      seenSp.set(sp, model.vocab[i]);
      const it = model.itemOf[i];
      if (it !== null) {
        if (seenIt.has(it)) {
          return `Two mons holding ${it} (${seenIt.get(it)}, ${model.vocab[i]}). Items must be unique.`;
        }
        seenIt.set(it, model.vocab[i]);
      }
    }
    return null;
  }, [model, teamIdxs]);

  const teamComplete = teamIdxs.length === TEAM_SIZE;

  const [copied, setCopied] = useState<boolean | "failed">(false);
  const handleCopyPaste = useCallback(() => {
    if (!model || teamIdxs.length === 0) return;
    const paste = buildPartialPaste(teamIdxs, model.vocab, speciesToSlug);
    // Clipboard access can be denied (insecure context, permissions); surface
    // that instead of leaving an unhandled rejection and a stuck label.
    navigator.clipboard.writeText(paste).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {
        setCopied("failed");
        setTimeout(() => setCopied(false), 1500);
      },
    );
  }, [model, teamIdxs]);

  // Heavy diagnostics — only compute when the team is valid + complete.
  // Everything is scored on the fitted model as-is (fieldWeight = 1).
  const diagnostics = useMemo(() => {
    if (!model || !teamComplete || uniquenessError) return null;
    const obs = teamObservables(model, teamSorted);
    const pjRows = pairwiseJRows(teamSorted, model.vocab, model.J, model.V);
    const swaps = rankSingleSwaps(model, {
      team: teamSorted,
      fieldWeight: 1,
      topN: TOP_SINGLE_SWAPS,
    });
    const greedy = greedyOptimize(model, {
      startingTeam: teamSorted,
      pinned: [],
      excluded: [],
      fieldWeight: 1,
      maxSwaps: GREEDY_MAX_SWAPS,
    });
    const finalObs = teamObservables(model, greedy.finalTeam);
    const finalSumJ = intraTeamSumJ(model.J, model.V, greedy.finalTeam);
    return { obs, pjRows, swaps, greedy, finalObs, finalSumJ };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, teamSorted.join(","), teamComplete, uniquenessError]);

  const corpus =
    model && teamComplete && !uniquenessError
      ? nearestObserved(teamSorted, teamCounts)
      : null;


  const corpusCaption = model
    ? `Reg ${model.regulation} · ${model.nCorpusTeams.toLocaleString()} teams`
    : undefined;

  return (
    <>
      <PageTitle
        eyebrow="VGC TOOL"
        h1="Team rater & analysis"
        rightCaption={corpusCaption}
      />

      {status === "loading" || model === null ? (
        <p style={{ color: "var(--lab-ink-muted)" }}>Loading model…</p>
      ) : <>

      <SectionLabel
        num="01"
        title={`Team · ${teamIdxs.length} of ${TEAM_SIZE} set`}
        right="pick a Pokémon and item for each of the six slots"
      />
      <RosterEditor
        model={model}
        roster={roster}
        onChange={(next) => setAnalysis({ roster: next })}
        itemActive
        teamSize={TEAM_SIZE}
        itemPlaceholder="item"
        emptyHint={null}
      />
      <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="lab-analyze-btn lab-copy-paste-btn"
          onClick={handleImport}
        >
          Import from clipboard
        </button>
        <button
          type="button"
          className="lab-analyze-btn lab-copy-paste-btn"
          onClick={handleCopyPaste}
          disabled={!teamComplete}
        >
          {copied === "failed"
            ? "Copy failed"
            : copied
              ? "Copied!"
              : "Copy pokepaste"}
        </button>
        <button
          type="button"
          className="lab-analyze-btn lab-copy-paste-btn"
          onClick={() => setAnalysis({ roster: [] })}
          disabled={roster.length === 0}
        >
          Clear all
        </button>
      </div>
      {importMsg?.error && (
        <div className="lab-form-error">{importMsg.error}</div>
      )}
      {importMsg?.warnings.map((w, i) => (
        <div className="lab-form-note" key={i}>{w}</div>
      ))}

      {uniquenessError && (
        <div className="lab-form-error">{uniquenessError}</div>
      )}
      {!teamComplete && !uniquenessError && (
        <p style={{ color: "var(--lab-ink-muted)", fontStyle: "italic" }}>
          Pick {TEAM_SIZE} Pokémon to analyze (have {teamIdxs.length}).
        </p>
      )}

      {teamComplete && !uniquenessError && diagnostics && (
        <>
          <SectionLabel num="02" title="Observables" />
          <StatStrip
            cells={[
              {
                label: "Score",
                value: formatSigned(diagnostics.obs.scoreRaw),
                sub: "model team score",
                tooltip:
                  "Hamiltonian-space score sign-flipped so higher = better. " +
                  "The model's data-calibrated team score." +
                  (corpusScoreIndex
                    ? ` ${percentileTitle(corpusScoreIndex.score, diagnostics.obs.scoreRaw)}.`
                    : ""),
              },
              {
                label: "Coherence",
                value: formatSigned(diagnostics.obs.coherence),
                sub: "intra-team coupling",
                tooltip:
                  "Σ J_ij over the C(team_size,2) unordered pairs. Positive " +
                  "= a synergistic archetype; negative = a balance team, " +
                  "whose strengths the pairwise model can't see." +
                  (corpusScoreIndex
                    ? ` ${percentileTitle(corpusScoreIndex.coherence, diagnostics.obs.coherence)}.`
                    : ""),
              },
              ...(corpus !== null
                ? [
                    {
                      label: "Corpus",
                      value:
                        corpus.delta === 0
                          ? `${corpus.count}×`
                          : `Δ${corpus.delta} (${corpus.count})`,
                      sub:
                        corpus.delta === 0
                          ? "exact roster seen"
                          : "to nearest observed",
                      tooltip:
                        "Distance to the nearest observed roster in the " +
                        "ingest corpus. Δ0 = the exact team appeared in " +
                        "tournament data; Δk = k swaps away from the " +
                        "nearest realized team.",
                    },
                  ]
                : []),
            ]}
          />

          <SectionLabel
            num="03"
            title="Pairwise coupling decomposition"
            right={`C(${TEAM_SIZE}, 2) = ${diagnostics.pjRows.length} unordered pairs · sorted by |Coupling|`}
          />
          <ScrollX>
            <PairwiseJTable rows={diagnostics.pjRows} />
          </ScrollX>

          <SectionLabel
            num="04"
            title="Top single swaps from this team"
            right="independent one-step alternatives · ranked by ΔScore"
          />
          {diagnostics.swaps.length === 0 ? (
            <p style={{ color: "var(--lab-ink-muted)", fontStyle: "italic" }}>
              No legal single swap exists from this team.
            </p>
          ) : (
            <SwapsTable
              swaps={diagnostics.swaps}
              teamIdx={teamSorted}
              model={model}
              teamCounts={teamCounts}
              onAcceptSwap={(out, inn) =>
                setAnalysis({
                  roster: roster.map((s) =>
                    s.feature === out
                      ? { site: model.siteOf[inn], feature: inn }
                      : s,
                  ),
                })
              }
            />
          )}

          <SectionLabel
            num="05"
            title="Greedy critique · single-swap chain"
            right={`converged in ${diagnostics.greedy.chain.length} of max ${GREEDY_MAX_SWAPS} swaps`}
          />
          <StatStrip
            cells={[
              {
                label: "Swaps taken",
                value: `${diagnostics.greedy.chain.length} / ${GREEDY_MAX_SWAPS}`,
                sub: "to local min",
              },
              {
                label: "Δ Score",
                value: formatSigned(
                  diagnostics.finalObs.scoreRaw - diagnostics.obs.scoreRaw,
                ),
                sub: "vs starting",
              },
              {
                label: "Δ Coherence",
                value: formatSigned(
                  diagnostics.finalSumJ - diagnostics.obs.coherence,
                ),
                sub: "vs starting",
              },
            ]}
          />
          {diagnostics.greedy.chain.length === 0 ? (
            <p style={{ color: "var(--lab-ink-muted)", fontStyle: "italic" }}>
              No improving single-swap exists: this team is a local
              maximum of Score.
            </p>
          ) : (
            <ChainTable
              startingTeam={teamSorted}
              chain={diagnostics.greedy.chain}
              startScore={diagnostics.obs.scoreRaw}
              startSumJ={diagnostics.obs.coherence}
              model={model}
              teamCounts={teamCounts}
              scoreIndex={corpusScoreIndex}
            />
          )}
        </>
      )}
      </>}
    </>
  );
}
export default AnalysisPage;
